import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import React, { useEffect, useState } from "react";
import { getItem } from "../../../../../utils/localStorage";
import { makeRequest2 } from "../../../../../utils/makeRequest";
import { toastify } from "../../../../../utils/toast";
import { getGLAccountsURL } from "../../../../../utils/urls";

const GLAccountsTab = ({
  formData,
  updateFormData,
  onNext,
  onPrevious,
  validation
}) => {
  const [glAccountsList, setGlAccountsList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGLAccounts();
  }, []);

  const fetchGLAccounts = async () => {
    try {
      setLoading(true);
      const facilityId = await getItem("selectedFacilityId");

      if (!facilityId) {
        toastify("No facility selected. Please select a facility first.", "error");
        return;
      }

      const response = await makeRequest2(`${getGLAccountsURL}/${facilityId}`, "GET");

      if (response.success) {
        console.log("GL Accounts data:", response.data);
        // Extract the actual accounts array from the response
        let accountsData = [];
        if (Array.isArray(response.data)) {
          accountsData = response.data;
        } else if (response.data && response.data.data) {
          accountsData = response.data.data;
        } else if (response.data && response.data.accounts) {
          accountsData = response.data.accounts;
        }

        console.log("Processed GL accounts data:", accountsData);

        // Filter for final accounts only (accounts that can be used for transactions)
        let finalAccounts = [];
        if (Array.isArray(accountsData)) {
          // Try to filter by isFinal first
          finalAccounts = accountsData.filter(account => account.isFinal === true);

          // If no accounts have isFinal=true, use all accounts
          if (finalAccounts.length === 0) {
            console.log("No accounts with isFinal=true found, using all accounts");
            finalAccounts = accountsData;
          }
        } else {
          finalAccounts = accountsData;
        }

        setGlAccountsList(finalAccounts);
        console.log("Final GL accounts set:", finalAccounts);
      } else {
        toastify("Failed to load GL accounts", "error");
      }
    } catch (error) {
      console.error("Error fetching GL accounts:", error);
      toastify("Failed to load GL accounts", "error");
    } finally {
      setLoading(false);
    }
  };

      const handleNext = () => {
      onNext();
    };
  
    const formatAccountOption = (account) => ({
      label: `${account.accountCode} - ${account.accountName}`,
      value: account._id
    });
  
    if (loading) {
      return (
        <div className="surface-card p-6 border-round-lg shadow-2">
          <div className="flex align-items-center mb-4">
            <i className="ti ti-calculator text-2xl text-primary mr-3"></i>
            <h3 className="text-xl font-semibold text-900 m-0">GL Accounts Configuration</h3>
          </div>
          <div className="text-center">
            <i className="ti ti-loader-2 text-6xl text-primary animate-spin"></i>
            <p className="mt-3 text-600">Loading GL accounts...</p>
          </div>
        </div>
      );
    }
  
    return (
      <React.Fragment>
        <div className="row">
          <div className="col-12 mb-4">
            <div className="surface-card p-4 border-round-lg shadow-1">
              <div className="flex align-items-center mb-3">
                <i className="ti ti-calculator text-2xl text-primary mr-3"></i>
                <h5 className="text-900 font-semibold m-0">GL Accounts Configuration</h5>
              </div>
              <p className="text-600 mb-4">
                Configure the General Ledger accounts for invoice and payment processing.
                These accounts will be used for automatic double-entry bookkeeping.
              </p>
  
              <div className="row">
                {/* Invoice Accounts */}
                <div className="col-12 mb-4">
                  <h6 className="text-900 font-semibold mb-3">
                    <i className="ti ti-file-invoice me-2"></i>
                    Invoice Accounts
                  </h6>
  
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label htmlFor="invoiceDebit" className="form-label">
                        Invoice Debit Account
                      </label>
                      <Dropdown
                        id="invoiceDebit"
                        value={formData.glAccounts.invoice.debit}
                        options={glAccountsList.map(formatAccountOption)}
                        onChange={(e) => updateFormData.updateGLAccounts({
                          invoice: {
                            ...formData.glAccounts.invoice,
                            debit: e.value
                          }
                        })}
                        placeholder="Select debit account for invoices"
                        className="w-100"
                        filter
                        showClear
                      />
                      <small className="text-muted">
                        Account to be debited when creating invoices (typically Accounts Receivable)
                      </small>
                    </div>
  
                    <div className="col-md-6 mb-3">
                      <label htmlFor="invoiceCredit" className="form-label">
                        Invoice Credit Account
                      </label>
                      <Dropdown
                        id="invoiceCredit"
                        value={formData.glAccounts.invoice.credit}
                        options={glAccountsList.map(formatAccountOption)}
                        onChange={(e) => updateFormData.updateGLAccounts({
                          invoice: {
                            ...formData.glAccounts.invoice,
                            credit: e.value
                          }
                        })}
                        placeholder="Select credit account for invoices"
                        className="w-100"
                        filter
                        showClear
                      />
                      <small className="text-muted">
                        Account to be credited when creating invoices (typically Revenue)
                      </small>
                    </div>
                  </div>
                </div>
  
                {/* Payment Accounts */}
                <div className="col-12 mb-4">
                  <h6 className="text-900 font-semibold mb-3">
                    <i className="ti ti-credit-card me-2"></i>
                    Payment Accounts
                  </h6>
  
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label htmlFor="paymentDebit" className="form-label">
                        Payment Debit Account
                      </label>
                      <Dropdown
                        id="paymentDebit"
                        value={formData.glAccounts.payment.debit}
                        options={glAccountsList.map(formatAccountOption)}
                        onChange={(e) => updateFormData.updateGLAccounts({
                          payment: {
                            ...formData.glAccounts.payment,
                            debit: e.value
                          }
                        })}
                        placeholder="Select debit account for payments"
                        className="w-100"
                        filter
                        showClear
                      />
                      <small className="text-muted">
                        Account to be debited when receiving payments (typically Cash/Bank)
                      </small>
                    </div>
  
                    <div className="col-md-6 mb-3">
                      <label htmlFor="paymentCredit" className="form-label">
                        Payment Credit Account
                      </label>
                      <Dropdown
                        id="paymentCredit"
                        value={formData.glAccounts.payment.credit}
                        options={glAccountsList.map(formatAccountOption)}
                        onChange={(e) => updateFormData.updateGLAccounts({
                          payment: {
                            ...formData.glAccounts.payment,
                            credit: e.value
                          }
                        })}
                        placeholder="Select credit account for payments"
                        className="w-100"
                        filter
                        showClear
                      />
                      <small className="text-muted">
                        Account to be credited when receiving payments (typically Accounts Receivable)
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
  
          {/* Navigation */}
          <div className="col-12 mt-4">
            <div className="d-flex justify-content-between">
              <Button
                label="Previous: Payment Methods"
                icon="ti ti-arrow-left"
                onClick={onPrevious}
                className="p-button-secondary"
              />
              <Button
                label="Next: Review & Submit"
                icon="ti ti-arrow-right"
                iconPos="right"
                onClick={handleNext}
                className="p-button-primary"
              />
            </div>
          </div>
        </div>
      </React.Fragment>  );
};

export default GLAccountsTab;